import { atom, useRecoilValue } from 'recoil';
import { getRecoil, setRecoil } from 'recoil-nexus';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';

import { UserDetailRes, deviceTypeIsMobile, useConnectDictAtom, useUserDetail } from '@/hooks';

import storage from './storage1';
import { shorten } from './string';
import { browserUid } from './browserUid';
import { localStorageEffect, registerSyncAtom } from './recoil';
import { deleteRefcode } from '@/components/ReferralCodeBtn/hooks';
import { useEffect, useRef } from 'react';
import { query2object } from './url';
import { useHistory, useLocation } from 'react-router-dom';
import { cacheLoginInfo, isInDebankApp } from './common';

const supportEnsDomains = ['.eth', '.xyz', '.art'];

type accountInfo = {
  nonce?: number;
  publicKey?: string;
  encryptedPrivateKey?: string;
  contract?: string;
  chainId?: string;
};

type StorageAddressInfo = {
  walletType?: string;
  sessionId?: string;
  account?: accountInfo;
  /**
   * @deprecated detail would be removed in the future
   */
  detail?: UserDetailRes;
  userInfo?: UserInfo;
  l2Balance?: number;
  // 1: beta 2. paid
  proStatus?: number;
  hasShownTrialModal?: boolean;
  hasXPClaimModalShown?: boolean;
};

export type AddressInfo = {
  isVerified?: boolean;
  address?: string;
} & StorageAddressInfo;

export let headUserInfoStr = '';

export let isLogout = false;

// https://github.com/facebookexperimental/Recoil/issues/728#issuecomment-729152549
// effect only run in atom initialize, could run multi times;
export const currentAddressAtom = atom<string | undefined>({
  key: 'currentAddressAtom',
  default: storage.current_address,
  effects_UNSTABLE: [
    localStorageEffect('current_address'),
    // syncEffect('current_address'),
    ({ onSet }) => {
      onSet(address => {
        setHeadUserInfoStr(address as string);
      });
    },
  ],
});

registerSyncAtom('current_address', currentAddressAtom);

export const connectedDictAtom = atom<Record<string, StorageAddressInfo>>({
  key: 'connectedDictAtom',
  default: storage.connected_dict ? cloneDeep(storage.connected_dict) : {},
  effects_UNSTABLE: [
    localStorageEffect('connected_dict'),
    ({ onSet }) => {
      onSet(() => {
        setHeadUserInfoStr();
      });
    },
  ],
});

registerSyncAtom('connected_dict', connectedDictAtom);

export function isEnsDomain(addr: string) {
  const subfix = addr?.toLowerCase().slice(-4);

  return supportEnsDomains.some(x => x === subfix);
}

export function isEthAddress(addr?: string): boolean {
  if (!addr) return false;
  const _addr = addr.toString().replace(/^0x/, '');

  return _addr.length === 40 && /^[0-9a-zA-Z]+$/.test(_addr);
}

/** 这个最好最后再判断，因为内部把 oa 去掉了比较，只要是 6 位以上的 数字 id 就是 true
    不是很准，但是历史原因，用得太多地方了，暂时不改了

    1. 是否是 system account ->
    2. 是否是 0x 用户 ->
    3. 是否是 official account
 */
export const isOfficalAddress = (address?: string | number) => {
  return !!address && /^[0-9a-z]{6}$/i.test(address?.toString().replace(/^0a/i, ''));
};

export const isSystemAddress = (address?: string) => {
  return !!address && /^0b\d{6,}$/i.test(address?.toLowerCase());
};

export function isEnsOrEthAddress(addr: string): boolean {
  return isEnsDomain(addr) || isEthAddress(addr);
}

export const getCurrentAddress = () => {
  return getRecoil(currentAddressAtom);
};

export const getCurrentAddressInfo = () => {
  const address = getRecoil(currentAddressAtom);

  if (address) {
    const info = getRecoil(connectedDictAtom)[address];

    return {
      ...info,
      isVerified: !!info?.sessionId,
      address,
    };
  }
};

export const getconnectedDict = () => {
  return getRecoil(connectedDictAtom);
};

export const connectWallet = async (_address: string, connector?: string) => {
  const address = _address.toLowerCase();
  if (!isEnsOrEthAddress(address)) {
    throw new Error('Invalid Address');
  }
  setRecoil(connectedDictAtom, old => ({
    ...old,
    [address]: {
      ...old?.[address],
      walletType: connector,
    },
  }));

  if (deviceTypeIsMobile) {
    setRecoil(currentAddressAtom, address);
  }
  setRecoil(connectedAddressAtom, address);
};

export const afterVerifySignAddress = async (verifiedAddress: string, loginInfo: any) => {
  setRecoil(connectedDictAtom, old => ({
    ...old,
    [verifiedAddress]: {
      ...old[verifiedAddress],
      sessionId: loginInfo.session_id,
      userInfo: loginInfo.userInfo,
    },
  }));
};

export const setCurrentAddress = (address: string) => {
  setRecoil(currentAddressAtom, address?.toLocaleLowerCase());
};

export const deleteCurrentAddress = (clearSession?: boolean) => {
  const address = getRecoil(currentAddressAtom);
  if (!address) {
    return;
  }

  setRecoil(currentAddressAtom, undefined);

  if (clearSession) {
    deleteAddress(address);
  }
};

export const deleteAddress = (address: string) => {
  const connectedDict = getRecoil(connectedDictAtom);
  setRecoil(connectedDictAtom, omit(connectedDict, address));
};

// 默认登出当前地址
export const logout = (_address?: string) => {
  const currentAddress = getRecoil(currentAddressAtom);
  const address = _address || currentAddress;
  if (!address) {
    return;
  }

  isLogout = true;
  const connectedDict = getRecoil(connectedDictAtom);
  const connectedList = Object.entries(connectedDict)
    .filter(([k, v]) => !!v.sessionId && k !== address)
    .sort(([, m], [, n]) =>
      !(m.userInfo?.desc?.usd_value || m.detail?.usd_value)
        ? 1
        : !(n.userInfo?.desc?.usd_value || n.detail?.usd_value)
          ? -1
          : (n.userInfo?.desc?.usd_value || n.detail?.usd_value || 0) -
            (m.userInfo?.desc?.usd_value || m.detail?.usd_value || 0),
    );

  // 如果是登出当前地址
  // 需要先设置 currentAddress，因为 useCurrentAddressInfo 中会检测有没有对应的 session_id
  if (!_address || _address === currentAddress) {
    setRecoil(currentAddressAtom, connectedList.length ? connectedList[0][0] : '');
  }

  deleteAddress(address);
  deleteRefcode(address);
};

export const shortenAddress = (addr: string, len = 6, endLen = 4) => {
  return isEnsDomain(addr) ? addr : shorten(addr, len, endLen);
};

export const getCredential = (address?: string) => {
  const addressInfo = getconnectedDict()?.[address!];

  const baseConfig = {
    random_at: browserUid?.random_at,
    random_id: browserUid?.random_id,
    user_addr: null,
  };

  const account = !addressInfo?.sessionId
    ? baseConfig
    : {
        ...baseConfig,
        session_id: addressInfo?.sessionId,
        user_addr: address,
        wallet_type: addressInfo?.walletType,
        is_verified: addressInfo?.sessionId ? true : undefined,
      };

  return JSON.stringify(account);
};

const setHeadUserInfoStr = (_address?: string) => {
  // trigger recoil effect
  headUserInfoStr = getCredential(_address || getCurrentAddress());
};

// init headUserInfoStr
// wait for recoil-nexus
export const initHeadUserInfoStr = () => setHeadUserInfoStr();

// connected Address
export const connectedAddressAtom = atom<string | undefined>({
  key: 'connectedAddressAtom',
  default: '',
});

export const getConnectAddress = () => {
  return getRecoil(connectedAddressAtom);
};

export const setConnectAddress = (address?: string) => {
  return setRecoil(connectedAddressAtom, address?.toLocaleLowerCase());
};

export const useCurrentUserId = () => {
  return useRecoilValue(currentAddressAtom);
};

export const useForceLogin = () => {
  const { currentAddress, sessionId, walletType } = cacheLoginInfo;
  const inDebankApp = isInDebankApp();
  const [, setConnectedDict] = useConnectDictAtom();
  const { data: userInfo } = useUserDetail(currentAddress);
  const history = useHistory();
  const location = useLocation();
  const isDirty = useRef(false);
  useEffect(() => {
    if (!inDebankApp) {
      return;
    }
    if (currentAddress && sessionId && !isDirty.current) {
      setHeadUserInfoStr(currentAddress);
      setConnectedDict(pre => ({
        ...pre,
        [currentAddress]: {
          // @ts-ignore
          detail: userInfo as UserDetailRes,
          sessionId: sessionId,
          walletType: walletType,
        },
      }));
      setCurrentAddress(currentAddress);
      const queryObj = query2object(location.search);
      const newQuery: Record<string, any> = omit(
        queryObj,
        'currentAddress',
        'sessionId',
        'walletType',
        'inDebankApp',
      );
      const newQueryStr = new URLSearchParams(newQuery).toString();
      history.replace({
        pathname: location.pathname,
        search: newQueryStr,
      });
      isDirty.current = true;
    }
  }, [
    currentAddress,
    history,
    inDebankApp,
    location.pathname,
    location.search,
    sessionId,
    setConnectedDict,
    userInfo,
    walletType,
  ]);
};
